import { UIEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useBoolean } from 'usehooks-ts';

import { SourceType } from '@/types/document';

import { getDataTestId } from '@/utils/browser';
import { getFileExtension } from '@/utils/file';

import { Image } from './Image';
import { PDFRenderer } from './PDFRenderer';

import {
  FileThumbnailContainer,
  ImageThumbnailContainer,
  WebView,
} from './File.styled';

type FileThumbnailProps = {
  src: File | string;
  type?: SourceType;
  onClick?: () => void;
  onPDFScrolledToBottom?: () => void;
};

const ESTIMATED_PADDING = 64;

export function FileThumbnail({
  src,
  type,
  onClick,
  onPDFScrolledToBottom,
}: FileThumbnailProps) {
  const fileContainerRef = useRef<HTMLElement>(null);
  const { value: isLoaded, setTrue: setLoaded } = useBoolean(false);

  const srcType = useMemo(() => {
    if (type) return type;

    if (typeof src === 'string') {
      return SourceType.Image;
    }
    const ext = getFileExtension(src);
    return ext === '.pdf' ? SourceType.PDF : SourceType.Image;
  }, [src, type]);

  const onScroll = useCallback(
    (e: UIEvent<HTMLElement> | HTMLElement) => {
      const target = e instanceof HTMLElement ? e : e.currentTarget;

      if (target && isLoaded) {
        const { scrollTop, scrollHeight, offsetHeight } = target;

        if (scrollTop >= scrollHeight - offsetHeight - ESTIMATED_PADDING) {
          onPDFScrolledToBottom?.();
        }
      }
    },
    [isLoaded, onPDFScrolledToBottom],
  );

  useEffect(() => {
    if (fileContainerRef.current) {
      onScroll(fileContainerRef.current);
    }
  }, [onScroll]);

  if (srcType === SourceType.Web) {
    return <WebView {...getDataTestId(`file-thumbnail`)} src={String(src)} />;
  }

  if (srcType === SourceType.Image) {
    const url = typeof src === 'string' ? src : URL.createObjectURL(src);

    return (
      <ImageThumbnailContainer
        {...getDataTestId(`file-thumbnail`)}
        onClick={onClick}
      >
        <Image src={url} />
      </ImageThumbnailContainer>
    );
  }

  return (
    <FileThumbnailContainer
      {...getDataTestId(`file-thumbnail`)}
      onClick={onClick}
      onScroll={onScroll}
      ref={fileContainerRef}
    >
      <PDFRenderer file={src} renderTextLayer={false} onLoaded={setLoaded} />
    </FileThumbnailContainer>
  );
}
